import { useCallback, useEffect, useMemo, useState } from 'react';
import { keepPreviousData, useMutation, useQuery, useQueryClient, UseQueryResult } from '@tanstack/react-query'; //-tech-debt-react-query;
import { CacheKey } from '../types/cache-keys';
import {
  adoptDcloudUiToken,
  DcloudDemoContent,
  DcloudEditSessionRequest,
  DcloudLinkStatus,
  DcloudUiTokenAdoption,
  DcloudScheduleRequest,
  DcloudSchedulingOptions,
  DcloudSession,
  DcloudSessionsResult,
  DCLOUD_LINK_RESULT_PARAM,
  DCLOUD_UI_TOKEN_PARAM,
  endDcloudSession,
  extendDcloudSession,
  fetchDcloudDemoCalendar,
  fetchDcloudDemoContents,
  fetchDcloudSessionHistory,
  fetchDcloudSchedulingOptions,
  fetchDcloudSessions,
  fetchDcloudLinkStatus,
  getPreferredDataCenter,
  scheduleDcloudSession,
  searchDcloudCatalog,
  setPreferredDataCenter,
  startDcloudLink,
  unlinkDcloudAccount,
  updateDcloudSession,
} from '../support/dcloudService';

/**
 * dCloud data is live pod state, so it is deliberately shorter lived than the
 * Toolbox default (5 minutes) and refetched when the manager regains focus.
 */
const DCLOUD_QUERY_OPTIONS = {
  staleTime: 1000 * 30,
  gcTime: 1000 * 60 * 5,
  retry: false as const,
};

/**
 * Data center preference, shared with the legacy dCloud calls through the
 * preferred_data_center cookie. Sessions only exist in the data center they
 * were scheduled in, so changing this changes what the lists return.
 */
export const useDcloudPreferredDataCenter = () => {
  const queryClient = useQueryClient();
  const [dataCenter, setDataCenter] = useState<string>(() => getPreferredDataCenter());

  const changeDataCenter = useCallback(
    (next: string) => {
      if (!next || next === dataCenter) return;

      setPreferredDataCenter(next);
      setDataCenter(next);

      queryClient.invalidateQueries({ queryKey: [CacheKey.DcloudSessions] });
      queryClient.invalidateQueries({ queryKey: [CacheKey.DcloudSessionHistory] });
      queryClient.invalidateQueries({ queryKey: [CacheKey.DcloudSchedulingOptions] });
      queryClient.invalidateQueries({ queryKey: [CacheKey.DcloudCatalogSearch] });
    },
    [dataCenter, queryClient],
  );

  return { dataCenter, setDataCenter: changeDataCenter };
};

export const useDcloudLinkStatus = (
  options?: Record<string, any>,
): UseQueryResult<DcloudLinkStatus, unknown> =>
  useQuery({
    queryKey: [CacheKey.DcloudLinkStatus],
    queryFn: () => fetchDcloudLinkStatus(),
    ...{
        ...DCLOUD_QUERY_OPTIONS,
        staleTime: 1000 * 60,
        ...options,
      },
  });

/**
 * The API origin, or '' when REACT_APP_API is relative. Deployed, this is the
 * same origin the app is served from; running the dev server it is not.
 */
const getApiOrigin = (): string => {
  try {
    return new URL(process.env.REACT_APP_API || '', window.location.origin).origin;
  } catch {
    return '';
  }
};

/**
 * Where the callback should send the browser back to.
 *
 * A relative path is resolved by the browser against whoever issued the 302,
 * which is the API. That is correct deployed, where the app and the API share an
 * origin, but sends the tab to the API host when the dev server on :3001 is
 * pointed at a remote API. So qualify it with our own origin when they differ.
 * The backend allows any origin in CORS_ALLOWED_ORIGINS, which covers
 * localhost:3001.
 */
const resolveDcloudNextUrl = (): string => {
  const path = `${window.location.pathname}${window.location.search}`;
  const apiOrigin = getApiOrigin();

  return apiOrigin && apiOrigin !== window.location.origin
    ? `${window.location.origin}${path}`
    : path;
};

/**
 * Starts the SSO link. The provider cannot complete a login inside fetch(), so
 * this resolves the authorization URL and then hands the tab to it. `nextUrl`
 * is where dCloud sends the browser back to; the backend rejects anything that
 * is not a relative path or an allowed Toolbox origin.
 */
type StartDcloudLinkVariables = {
  nextUrl?: string;
  /** Sign in against Topology Builder's provider rather than the portal's. */
  backend?: 'tb_idp';
};

export const useStartDcloudLink = () =>
  useMutation<void, Error, StartDcloudLinkVariables | void>({
    mutationFn: async (variables) => {
      const options = (variables || {}) as StartDcloudLinkVariables;
      const nextUrl = options.nextUrl || resolveDcloudNextUrl();

      const login = await startDcloudLink(nextUrl, options.backend);

      if (!login?.authorization_url) {
        throw new Error('dCloud did not return an authorization URL.');
      }

      window.location.assign(login.authorization_url);
    },
  });

export const useUnlinkDcloud = () => {
  const queryClient = useQueryClient();

  return useMutation<DcloudLinkStatus, Error, void>({
    mutationFn: () => unlinkDcloudAccount(),
    onSuccess: (status) => {
      queryClient.setQueryData([CacheKey.DcloudLinkStatus], status);
      queryClient.invalidateQueries({ queryKey: [CacheKey.DcloudSessions] });
      queryClient.invalidateQueries({ queryKey: [CacheKey.DcloudSessionHistory] });
    },
  });
};

/**
 * Store a token copied out of an authenticated dCloud or Topology Builder tab.
 *
 * Everything the token gates is invalidated, not just link status: Topology
 * Builder in particular was answering 401 with the previous token and its cached
 * capabilities/current-user results say the caller has no access.
 */
export const useAdoptDcloudUiToken = () => {
  const queryClient = useQueryClient();

  return useMutation<DcloudLinkStatus, Error, DcloudUiTokenAdoption>({
    mutationFn: (adoption) => adoptDcloudUiToken(adoption),
    onSuccess: (status) => {
      queryClient.setQueryData([CacheKey.DcloudLinkStatus], status);
      queryClient.invalidateQueries({ queryKey: [CacheKey.DcloudSessions] });
      queryClient.invalidateQueries({ queryKey: [CacheKey.DcloudSessionHistory] });
      queryClient.invalidateQueries({ queryKey: [CacheKey.DcloudTopologyCapabilities] });
      queryClient.invalidateQueries({ queryKey: [CacheKey.DcloudTopologyCurrentUser] });
      queryClient.invalidateQueries({ queryKey: [CacheKey.DcloudTopologies] });
    },
  });
};

export type DcloudLinkNotice = {
  result: 'success' | 'error';
  reason?: string;
  /**
   * 'unavailable' when the link succeeded but the chained dCloud ui-token mint
   * did not. Sign-in worked; dCloud calls fall back to the IdP token.
   */
  uiToken?: 'unavailable';
} | null;

/**
 * Reads the ?dcloud_link=success|error&reason=... the callback appends when it
 * returns the browser here, then strips those params so a refresh does not
 * replay the banner.
 */
export const useDcloudLinkCallbackNotice = (): {
  notice: DcloudLinkNotice;
  dismiss: () => void;
} => {
  const queryClient = useQueryClient();
  const [notice, setNotice] = useState<DcloudLinkNotice>(null);

  useEffect(() => {
    const params = new URLSearchParams(window.location.search);
    const result = params.get(DCLOUD_LINK_RESULT_PARAM);

    if (result !== 'success' && result !== 'error') {
      return;
    }

    setNotice({
      result,
      reason: params.get('reason') || undefined,
      uiToken:
        params.get(DCLOUD_UI_TOKEN_PARAM) === 'unavailable'
          ? 'unavailable'
          : undefined,
    });

    if (result === 'success') {
      queryClient.invalidateQueries({ queryKey: [CacheKey.DcloudLinkStatus] });
      queryClient.invalidateQueries({ queryKey: [CacheKey.DcloudSessions] });
      queryClient.invalidateQueries({ queryKey: [CacheKey.DcloudSessionHistory] });
    }

    params.delete(DCLOUD_LINK_RESULT_PARAM);
    params.delete(DCLOUD_UI_TOKEN_PARAM);
    params.delete('reason');

    const query = params.toString();

    window.history.replaceState(
      window.history.state,
      '',
      `${window.location.pathname}${query ? `?${query}` : ''}${window.location.hash}`,
    );
  }, [queryClient]);

  return { notice, dismiss: () => setNotice(null) };
};

export const useDcloudSessions = (
  dataCenter: string,
  options?: Record<string, any>,
): UseQueryResult<DcloudSessionsResult, unknown> =>
  useQuery({
    queryKey: [CacheKey.DcloudSessions, dataCenter],
    queryFn: () => fetchDcloudSessions(dataCenter),
    ...{
        ...DCLOUD_QUERY_OPTIONS,
        refetchOnWindowFocus: true,
        ...options,
      },
  });

export const useDcloudSessionHistory = (
  dataCenter: string,
  options?: Record<string, any>,
): UseQueryResult<DcloudSession[], unknown> =>
  useQuery({
    queryKey: [CacheKey.DcloudSessionHistory, dataCenter],
    queryFn: () => fetchDcloudSessionHistory(dataCenter),
    ...{
        ...DCLOUD_QUERY_OPTIONS,
        staleTime: 1000 * 60 * 5,
        ...options,
      },
  });

export const useDcloudSchedulingOptions = (
  dataCenter: string,
  options?: Record<string, any>,
): UseQueryResult<DcloudSchedulingOptions, unknown> =>
  useQuery({
    queryKey: [CacheKey.DcloudSchedulingOptions, dataCenter],
    queryFn: () => fetchDcloudSchedulingOptions(dataCenter),
    ...{
        ...DCLOUD_QUERY_OPTIONS,
        staleTime: 1000 * 60 * 10,
        ...options,
      },
  });

export const useDcloudCatalogSearch = (
  text: string,
  dataCenter: string,
  options?: Record<string, any>,
): UseQueryResult<any[], unknown> => {
  const search = (text || '').trim();

  return useQuery({
    queryKey: [CacheKey.DcloudCatalogSearch, dataCenter, search],
    queryFn: () => searchDcloudCatalog(search, dataCenter),
    ...{
        ...DCLOUD_QUERY_OPTIONS,
        staleTime: 1000 * 60 * 10,
        enabled: search.length >= 2,
        placeholderData: keepPreviousData,
        ...options,
      },
  });
};

/**
 * The saved demos a lab can be published from, in one data centre.
 *
 * Keyed by data centre and never shared across them: a demo uid means nothing in a
 * data centre that does not hold it, so a cache hit from the wrong one would offer
 * an admin templates they cannot schedule. Held for longer than session state
 * because a demo catalogue changes when somebody saves a topology, not by the minute.
 */
export const useDcloudDemoContents = (
  dataCenter: string,
  options?: Record<string, any>,
): UseQueryResult<DcloudDemoContent[], unknown> =>
  useQuery({
    queryKey: [CacheKey.DcloudDemoContents, dataCenter],
    queryFn: () => fetchDcloudDemoContents(dataCenter),
    ...{
        ...DCLOUD_QUERY_OPTIONS,
        staleTime: 1000 * 60 * 10,
        enabled: !!dataCenter,
        ...options,
      },
  });

export const useDcloudDemoCalendar = (
  demoId: number | null,
  start: Date | null,
  end: Date | null,
  dataCenter: string,
  options?: Record<string, any>,
) =>
  useQuery({
    queryKey: [
      CacheKey.DcloudDemoCalendar,
      dataCenter,
      demoId,
      start?.toISOString() || '',
      end?.toISOString() || '',
    ],
    queryFn: () => fetchDcloudDemoCalendar(demoId as number, start as Date, end as Date, dataCenter),
    ...{
        ...DCLOUD_QUERY_OPTIONS,
        enabled: Boolean(demoId && start && end),
        ...options,
      },
  });

const useSessionMutationOptions = (dataCenter: string) => {
  const queryClient = useQueryClient();

  return useMemo(
    () => ({
      onSuccess: () => {
        queryClient.invalidateQueries({ queryKey: [CacheKey.DcloudSessions, dataCenter] });
        queryClient.invalidateQueries({ queryKey: [CacheKey.DcloudSessionHistory, dataCenter] });
      },
    }),
    [dataCenter, queryClient],
  );
};

export const useScheduleDcloudSession = (dataCenter: string) => {
  const mutationOptions = useSessionMutationOptions(dataCenter);

  return useMutation<any, Error, DcloudScheduleRequest>({
    ...mutationOptions,
    mutationFn: (payload) => scheduleDcloudSession(payload, dataCenter),
  });
};

export const useUpdateDcloudSession = (dataCenter: string) => {
  const mutationOptions = useSessionMutationOptions(dataCenter);

  return useMutation<any, Error, { sessionId: number; payload: DcloudEditSessionRequest }>({
    ...mutationOptions,
    mutationFn: ({ sessionId, payload }) => updateDcloudSession(sessionId, payload, dataCenter),
  });
};

export const useEndDcloudSession = (dataCenter: string) => {
  const mutationOptions = useSessionMutationOptions(dataCenter);

  return useMutation<any, Error, number>({
    ...mutationOptions,
    mutationFn: (sessionId) => endDcloudSession(sessionId, dataCenter),
  });
};

export const useExtendDcloudSession = (dataCenter: string) => {
  const mutationOptions = useSessionMutationOptions(dataCenter);

  return useMutation<any, Error, { session: DcloudSession; extraMinutes: number }>({
    ...mutationOptions,
    mutationFn: ({ session, extraMinutes }) => extendDcloudSession(session, extraMinutes, dataCenter),
  });
};
